package com.yendo.mx.ui.home

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.yendo.mx.models.Producto
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.SeccionHome
import com.yendo.mx.repository.HomeRepository
import com.yendo.mx.utils.getAhora
import com.yendo.mx.ui.components.RestauranteRatingBadge
import com.yendo.mx.utils.BackHandler
import com.yendo.mx.ui.theme.RosaYendo
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ExplorarSeccionScreen(
    seccion: SeccionHome,
    idZonaActual: String,
    colorTextoPrincipal: Color,
    colorFondoTarjetas: Color,
    onBack: () -> Unit,
    onRestauranteClick: (RestauranteFirebase) -> Unit,
    onProductoClick: (Producto, RestauranteFirebase) -> Unit, // 🚀 CAMBIADO PARA RECIBIR DUEÑO
    procesandoAccion: (Boolean) -> Unit = {} // 🚀 OPCIONAL PARA FEEDBACK
) {
    val isDark = isSystemInDarkTheme()
    val coroutineScope = rememberCoroutineScope()
    val colorFondoGlobal = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    // 🚀 INTERCEPTOR DEL BOTÓN ATRÁS
    BackHandler(enabled = true) {
        onBack()
    }

    // 🚀 DETECTAMOS QUÉ TIPO DE DATOS VAMOS A CARGAR
    val esSeccionProductos = seccion.tipoBloque == "CARRUSEL_CUADRADO"

    // 🚀 CONSULTAS DINÁMICAS (Solo se ejecuta la que aplica, limite = 0 para traer TODOS)
    val restaurantesReales by remember(seccion, idZonaActual) {
        if (!esSeccionProductos) HomeRepository.obtenerNegociosParaSeccion(seccion, idZonaActual, limite = 0) else flowOf(emptyList())
    }.collectAsState(initial = emptyList())

    val productosReales by remember(seccion, idZonaActual) {
        if (esSeccionProductos) HomeRepository.obtenerProductosParaSeccion(seccion, idZonaActual, limite = 0) else flowOf(emptyList())
    }.collectAsState(initial = emptyList())

    Scaffold(
        containerColor = colorFondoGlobal,
        topBar = {
            TopAppBar(
                title = { Text(seccion.titulo, fontWeight = FontWeight.Black, color = colorTextoPrincipal) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Regresar", tint = colorTextoPrincipal)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoGlobal)
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            if (esSeccionProductos) {
                // 🍔 RENDERIZAMOS PRODUCTOS
                items(productosReales, key = { it.id }) { producto ->
                    Card(
                        modifier = Modifier.fillMaxWidth().clickable {
                            coroutineScope.launch {
                                procesandoAccion(true)
                                try {
                                    val restDoc = Firebase.firestore.collection("Negocios").document(producto.idRestaurante).get()
                                    val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                    
                                    val ahora = getAhora()
                                    val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                                    val esVisible = rest.operacion.visible && rest.operacion.activoComercio && !rest.operacion.bloqueadoPorAdmin && !estaPausado

                                    if (esVisible) {
                                        onProductoClick(producto, rest)
                                    } else {
                                        println("⚠️ Negocio no visible.")
                                    }
                                } catch (e: Exception) { println("🚨 Error validando producto: ${e.message}") }
                                finally { procesandoAccion(false) }
                            }
                        },
                        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                        elevation = CardDefaults.cardElevation(2.dp),
                        shape = RoundedCornerShape(16.dp)
                    ) {
                        Box {
                            Row(modifier = Modifier.fillMaxWidth().padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                AsyncImage(
                                    model = ImageRequest.Builder(LocalPlatformContext.current).data(producto.foto).crossfade(true).build(),
                                    contentDescription = producto.nombre, contentScale = ContentScale.Crop,
                                    modifier = Modifier.size(80.dp).clip(RoundedCornerShape(12.dp)).background(Color.Gray.copy(0.1f))
                                )
                                Spacer(modifier = Modifier.width(16.dp))
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(producto.nombre, fontWeight = FontWeight.Bold, fontSize = 15.sp, color = colorTextoPrincipal, maxLines = 2, overflow = TextOverflow.Ellipsis)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(producto.descripcion, fontSize = 13.sp, color = colorTextoSecundario, maxLines = 2, overflow = TextOverflow.Ellipsis)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text("$${producto.precio}", fontWeight = FontWeight.Black, fontSize = 15.sp, color = RosaYendo)
                                }
                            }
                            
                            // 🚀 BOTON DE AGREGAR (+) MEJORA UX
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomEnd)
                                    .padding(8.dp)
                                    .size(32.dp)
                                    .background(RosaYendo, CircleShape)
                                    .border(2.dp, Color.White, CircleShape),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(Icons.Default.Add, null, tint = Color.White, modifier = Modifier.size(20.dp))
                            }

                            // 🚀 INDICADOR DE NO DISPONIBLE
                            if (!producto.operacion.disponible) {
                                Box(
                                    modifier = Modifier.matchParentSize().background(Color.Black.copy(alpha = 0.4f)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Surface(color = Color.Black.copy(alpha = 0.7f), shape = RoundedCornerShape(8.dp)) {
                                        Text("NO DISPONIBLE", color = Color.White, fontSize = 10.sp, fontWeight = FontWeight.Black, modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp))
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                // 🏪 RENDERIZAMOS RESTAURANTES
                items(restaurantesReales, key = { it.id }) { restaurante ->
                    val ahora = getAhora()
                    val estaPausado = restaurante.operacion.pausadoManualmente && restaurante.operacion.pausaHasta > ahora
                    
                    val estaAbierto = restaurante.operacion.estado == "ACTIVO" && 
                                    !restaurante.operacion.bloqueadoPorAdmin && 
                                    !estaPausado &&
                                    restaurante.operacion.visible &&
                                    restaurante.operacion.activoComercio // 🚀 VISIBILIDAD DUAL

                    val permiteProgramar = restaurante.operacion.programadoActivo && restaurante.operacion.visible && restaurante.operacion.activoComercio && !restaurante.operacion.bloqueadoPorAdmin && !estaPausado

                    val (estadoTexto, colorEstado) = when {
                        !estaAbierto && !permiteProgramar -> "Cerrado" to Color.Red
                        !estaAbierto && permiteProgramar -> "Solo programación" to Color(0xFFFF9800)
                        else -> "Abierto" to Color(0xFF4CAF50)
                    }

                    val categorias = restaurante.infoPublica.subcategorias.take(2).joinToString(", ")

                    Card(
                        modifier = Modifier.fillMaxWidth().clickable {
                            val ahoraActual = getAhora()
                            val estaPausadoActual = restaurante.operacion.pausadoManualmente && restaurante.operacion.pausaHasta > ahoraActual
                            val esVisible = restaurante.operacion.visible && restaurante.operacion.activoComercio && !restaurante.operacion.bloqueadoPorAdmin && !estaPausadoActual

                            if (esVisible) {
                                onRestauranteClick(restaurante)
                            } else {
                                println("⚠️ Negocio no visible.")
                            }
                        },
                        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
                        elevation = CardDefaults.cardElevation(2.dp),
                        shape = RoundedCornerShape(16.dp)
                    ) {
                        Row(modifier = Modifier.fillMaxWidth().padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                            AsyncImage(
                                model = ImageRequest.Builder(LocalPlatformContext.current).data(restaurante.infoPublica.logotipo).crossfade(true).build(),
                                contentDescription = restaurante.infoPublica.nombre, contentScale = ContentScale.Crop,
                                modifier = Modifier.size(80.dp).clip(RoundedCornerShape(12.dp)).background(Color.Gray.copy(0.1f))
                            )
                            Spacer(modifier = Modifier.width(16.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text(restaurante.infoPublica.nombre, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(categorias.ifEmpty { "Restaurante" }, fontSize = 13.sp, color = colorTextoSecundario, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                Spacer(modifier = Modifier.height(4.dp))
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 4.dp)) {
                                    RestauranteRatingBadge(reputacion = restaurante.reputacion)
                                    Spacer(Modifier.width(8.dp))
                                    Text(estadoTexto, fontSize = 12.sp, color = colorEstado, fontWeight = FontWeight.Medium)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}